import { h, Component } from 'preact';
import { accessNested, removeClass, joinClasses } from 'utils/utils';
import pure from 'utils/pure';
import { SvgMarker } from 'components/shared/svgIcons/svgIcons';
import HTML from 'components/core/html/html';
import s from 'components/shared/gmap/customMarker.sss';

import Store from 'store/store';

// Transform a node into another
function transformNode(node, ref) {
	if (node instanceof Text) {
		return node.nodeValue = ref.nodeValue;
	}
	let i;
	// --- Match attributes ---
	let attrs = node.attributes;
	const remove = [];
	// Store extra attributes and remove them later as we are looping over them
	for (i = attrs.length; i--;) {
		const attrName = attrs[i].nodeName;
		if (!ref.hasAttribute(attrName)) {
			remove.push(attrName);
		}
	}
	remove.forEach(attr => node.removeAttribute(attr));

	attrs = ref.attributes;
	for (i = attrs.length; i--;) {
		const attr = attrs[i];
		node.setAttribute(attr.nodeName, attr.nodeValue);
	}
	// --- Match children ---
	const nodes = node.childNodes;
	const l1 = nodes.length;
	const newNodes = ref.childNodes;
	const l2 = newNodes.length;
	for (i = 0; i < l1; i++) {
		const from = nodes[i];
		// Extra nodes
		if (i >= l2) {
			node.removeChild(from);
			continue;
		}
		const to = newNodes[i];
		// Try to transform every children if possible
		if (from.constructor.name === to.constructor.name && from.nodeName === to.nodeName) {
			transformNode(from, to);
		} else {
			node.replaceChild(to.cloneNode(true), from);
		}
	}
	// New nodes
	for (; i < l2; i++) {
		node.appendChild(newNodes[i].cloneNode(true));
	}
}

function getPos(event) {
	let touch = (event && event.touches && event.touches[0]) || {};
	let x = 0, y = 0;
	if ('clientX' in event) {
		x = event.clientX;
	} else if ('clientX' in touch) {
		x = touch.clientX;
	}
	if ('clientY' in event) {
		y = event.clientY;
	} else if ('clientY' in touch) {
		y = touch.clientY;
	}
	return [x, y];
}

// Mouse handlers
// Moving out of the function so that there is only one global mouse move / up handler instead of one per marker
let grabbedList = [];
function grabMarker(el) {
	if (grabbedList.indexOf(el) === -1) {
		grabbedList.push(el);
	}
}
function releaseMarker(el) {
	let index = grabbedList.indexOf(el);
	if (index !== -1) {
		grabbedList.splice(index, 1);
	}
}
function mousemove(e) {
	grabbedList.forEach(el => el.mousemove(e));
}
function mouseup(e) {
	grabbedList.forEach(el => el.mouseup(e));
}

if (typeof document !== 'undefined') {
	document.addEventListener('mousemove', mousemove);
	document.addEventListener('touchmove', mousemove);
	document.addEventListener('mouseup', mouseup);
	document.addEventListener('touchend', mouseup);
}

export default @pure class CustomMarker extends Component {

	constructor(props) {
		super();
		this.mousedown = this.mousedown.bind(this);
		this.mousemove = this.mousemove.bind(this);
		this.mouseup = this.mouseup.bind(this);
		this.draw = this.draw.bind(this);

		this.mounted = false;
		this.onMap = false;
		this.currentDom = null;
		this.offset = [0, 0];
		this.cancelClick = false;
		this.grabbedAt = null;

		this.updateLatLng(props);

		// Caches the image root here
		this.imageRoot = accessNested(Store.get(), 'appData.urls.roots.image', '');

		this.s = s;
	}

	componentDidMount() {
		this.mount();
	}

	componentDidUpdate(props) {
		if (this.props.isStatic) {
			return;
		}

		let previouslyMounted = this.mounted;
		if (!this.mounted) {
			this.mount();
			// Still not mounted
			if (!this.mounted) {
				return;
			}
		}

		this.add();
		// Test if the content have actually changed
		// Ok in ie9+
		if (!this.currentDom.isEqualNode(this.dom)) {
			const newCopy = this.dom.cloneNode(true);
			removeClass(newCopy, this.s.hidden);
			this.currentDom = newCopy;
			// Match elements instead of replacing them (makes transition work)
			transformNode(this.markerDom, newCopy);
			// Style is reset so make sure to replace it
			this.draw();
		}
		if (!previouslyMounted || props.latlng !== this.props.latlng || props.geo !== this.props.geo) {
			this.updateLatLng();
			this.draw();
		}
	}

	updateLatLng(props = this.props) {
		if (props.latlng) {
			this.latlng = props.latlng;
		} else if (props.geo) {
			this.latlng = props.geo.toGoogleLatLng();
		}
	}

	componentWillUnmount() {
		this.removeListeners();
		this.remove();
	}

	mount() {
		if (this.mounted || this.props.isStatic || !this.dom || !window.google) {
			return;
		}

		this.mounted = true;

		this.currentDom = this.dom.cloneNode(true);
		removeClass(this.currentDom, this.s.hidden);

		const dom = this.dom.cloneNode(true);
		removeClass(dom, this.s.hidden);

		const marker = this.marker = new google.maps.OverlayView();
		marker.onAdd = function() {
			const panes = this.getPanes();
			if (!panes) return;
			this.parent = panes.overlayImage;
			this.parent.appendChild(dom);
		};
		marker.onRemove = () => {
			try {
				dom.parentNode.removeChild(dom);
			} catch (e) {
				console.error('Maps custom marker - element not in the dom', e);
			}
		};
		marker.draw = this.draw;

		// NOTE: DO NOT ADD CUSTOM PROPERTIES TO MARKERS - prevents them from being removed for some reason
		// marker.dom = dom;
		this.markerDom = dom;
		this.addListeners();

		this.onMap = true;
		marker.setMap(this.props.map);
	}

	mousedown(event) {
		if (this.grabbedAt) {
			return;
		}
		this.grabbedAt = getPos(event);
		this.cancelClick = false;
		grabMarker(this);
		if (this.props.draggable && this.props.map) {
			this.props.map.setOptions({ gestureHandling: 'none' });
		}
	}

	mousemove(event) {
		if (!this.grabbedAt) {
			return;
		}
		let pos = getPos(event);
		const dx = pos[0] - this.grabbedAt[0];
		const dy = pos[1] - this.grabbedAt[1];
		if (!this.cancelClick) {
			const dist = dx * dx + dy * dy;
			if (dist > 200) {
				this.cancelClick = true;
			}
		}
		if (this.cancelClick && this.props.draggable) {
			this.offset[0] = dx;
			this.offset[1] = dy;
			this.draw();
		}
	}

	mouseup(event) {
		if (!this.grabbedAt) {
			return;
		}
		event.preventDefault();
		this.grabbedAt = null;
		releaseMarker(this);
		if (this.props.draggable && this.props.map) {
			this.props.map.setOptions({ gestureHandling: 'auto' });
		}

		if (!this.cancelClick) {
			this.trigger('onClick', event);
		} else if (this.props.draggable) {
			const marker = this.marker;
			const proj = marker.getProjection();
			const srcP = proj.fromLatLngToDivPixel(this.latlng);
			srcP.x += this.offset[0];
			srcP.y += this.offset[1];
			const resultLatLng = proj.fromDivPixelToLatLng(srcP);
			this.trigger('ondragend', resultLatLng, this.offset);
			this.latlng = resultLatLng;
			this.offset = [0, 0];
		}
	}

	draw() {
		const marker = this.marker;
		const proj = marker.getProjection();
		if (!proj) {
			return;
		}
		const p = proj.fromLatLngToDivPixel(this.latlng);
		if (p) {
			this.markerDom.style.left = p.x + this.offset[0] + 'px';
			this.markerDom.style.top = p.y + this.offset[1] + 'px';
		}
	}

	trigger(event, ...data) {
		if (event && this.props[event] && typeof this.props[event] === 'function') {
			this.props[event](...data);
		}
	}

	addListeners() {
		google.maps.event.addDomListener(this.markerDom, 'mousedown', this.mousedown);
		google.maps.event.addDomListener(this.markerDom, 'touchstart', this.mousedown, { passive: true });
	}
	removeListeners() {
		if (window.google && this.markerDom) {
			try {
				google.maps.event.removeDomListener(this.markerDom, 'mousedown', this.mousedown);
				google.maps.event.removeDomListener(this.markerDom, 'touchstart', this.mousedown);
			} catch(e) { /* */ }
		}
	}

	remove() {
		if (this.marker && this.onMap) {
			this.marker.setMap(null);
		}
		this.onMap = false;
	}

	add() {
		if (this.marker && !this.onMap) {
			this.marker.setMap(this.props.map);
		}
		this.onMap = true;
	}

	refresh() {
		if (!this.marker) return;
		this.marker.setMap(null);
		if (this.onMap) this.marker.setMap(this.props.map);
	}

	renderBackground() {
		let useCustomMarker = this.props.useCustomMarker;

		let BgElem = useCustomMarker ? HTML : SvgMarker;
		let style = {};
		if (useCustomMarker && typeof useCustomMarker === 'string') {
			let url = useCustomMarker;
			if (!url.match(/^(https?:)?\/\//)) {
				url = this.imageRoot + '/' + url;
			}
			style.backgroundImage = 'url(' + url + ')';
		}

		return <BgElem class={joinClasses(this.s.background, this.props.backgroundClass, useCustomMarker && this.s.customBackground)} style={style} />;
	}

	render({ children, active, useCustomMarker, bottomAnchor, map, latlng, isStatic, geo, ...props }) {

		// NOTE :
		// --------------------------------------
		// To use customMarker, there must be a file called 'map_marker.png' in $imageRoot

		const className = joinClasses(
			this.s.marker,
			props.class,
			active && this.s.active,
			!isStatic && this.s.hidden,
			useCustomMarker && this.s.customMarker,
			useCustomMarker && !bottomAnchor && this.s.centerAnchor
		);
		delete props.class;

		return (
			<div
				class={className}
				onClick={e => this.trigger('onClick', e)}
				ref={e => this.dom = e}
				{...props}
			>
				{this.renderBackground()}
				{children}
			</div>
		);
	}

}
